/**
 * The user table (T4.5) — create, edit, reset a password, deactivate.
 *
 * Rules the API enforces and this screen makes visible rather than hides:
 *
 *   - Your own row offers no role change, no deactivation and no admin reset.
 *     These controls are ABSENT, not disabled: the API refuses each with a 422
 *     and an offered control that then fails is worse than none. Your own
 *     password is changed on the Account screen, which asks for the current one.
 *   - A deactivated user's access grants still exist. The row says so, and the
 *     confirmation says it before deactivating.
 *   - A password an admin sets is temporary: the person must replace it at
 *     their next sign-in. The dialogue says so.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth';
import { useTerms } from '../../lib/terms';
import { useDocumentTitle } from '../../lib/documentTitle';
import {
  detailList,
  errorText,
  useCreateUser,
  useResetPassword,
  useUpdateUser,
  useUsers,
  type ManagedUser,
} from '../../lib/admin';
import type { GlobalRole } from '../../lib/auth';
import { Dialog } from '../versions/Dialog';
import { ConfirmDialog, FormError, ProblemList, formatWhen } from './ui';

const ROLE_LABEL: Record<GlobalRole, string> = { admin: 'Administrator', user: 'User' };

type Modal =
  | { kind: 'create' }
  | { kind: 'edit'; user: ManagedUser }
  | { kind: 'reset'; user: ManagedUser }
  | { kind: 'deactivate'; user: ManagedUser }
  | { kind: 'reactivate'; user: ManagedUser };

export function UserTable() {
  const terms = useTerms();
  const { user: me } = useAuth();
  const { data, isPending, isError, error } = useUsers();
  const [modal, setModal] = useState<Modal | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useDocumentTitle('Users', terms.plural('blueprint'));

  const users = [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));
  const close = () => setModal(null);

  return (
    <div className="sheet adm-sheet">
      <div className="masthead">
        <div>
          <h1>Users</h1>
          <p className="meta">
            Who can sign in. What each person can open is set on the Access screen, per{' '}
            {terms.noun('blueprint')}.
          </p>
        </div>
        <button type="button" className="btn primary" onClick={() => setModal({ kind: 'create' })}>
          Add user
        </button>
      </div>

      {notice ? (
        <p className="status adm-notice" role="status">
          {notice}
        </p>
      ) : null}

      {isPending ? (
        <div className="empty" role="status" aria-live="polite">
          Loading…
        </div>
      ) : isError ? (
        <div className="empty" role="alert">
          Could not load the users.
          <br />
          {errorText(error)}
        </div>
      ) : (
        <div className="adm-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email address</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">Last signed in</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} className={u.isActive ? undefined : 'adm-inactive'}>
                    <th scope="row" className="adm-name">
                      {u.name}
                      {isMe ? <span className="adm-tag">You</span> : null}
                    </th>
                    <td className="adm-email">{u.email}</td>
                    <td>{ROLE_LABEL[u.globalRole]}</td>
                    <td>
                      {u.isActive ? 'Active' : <span className="adm-tag warn">Deactivated</span>}
                      {u.mustChangePassword ? (
                        <span className="adm-sub">Must set a new password at next sign-in</span>
                      ) : null}
                    </td>
                    <td>{formatWhen(u.lastLoginAt)}</td>
                    <td className="adm-actions">
                      <button type="button" className="btn" onClick={() => setModal({ kind: 'edit', user: u })}>
                        Edit
                      </button>
                      {!isMe ? (
                        <>
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setModal({ kind: 'reset', user: u })}
                          >
                            Reset password
                          </button>
                          {u.isActive ? (
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setModal({ kind: 'deactivate', user: u })}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setModal({ kind: 'reactivate', user: u })}
                            >
                              Reactivate
                            </button>
                          )}
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserDialog
        open={modal?.kind === 'create'}
        onClose={close}
        onDone={(u) => {
          close();
          setNotice(`${u.name} was added. They must choose a new password when they first sign in.`);
        }}
      />
      <EditUserDialog
        user={modal?.kind === 'edit' ? modal.user : null}
        isMe={modal?.kind === 'edit' && modal.user.id === me?.id}
        onClose={close}
        onDone={(u) => {
          close();
          setNotice(`${u.name} was updated.`);
        }}
      />
      <ResetPasswordDialog
        user={modal?.kind === 'reset' ? modal.user : null}
        onClose={close}
        onDone={(u) => {
          close();
          setNotice(`${u.name}'s password was reset and every session they had was ended.`);
        }}
      />
      <ActiveDialog
        user={modal?.kind === 'deactivate' || modal?.kind === 'reactivate' ? modal.user : null}
        activate={modal?.kind === 'reactivate'}
        onClose={close}
        onDone={(u, activated) => {
          close();
          setNotice(activated ? `${u.name} was reactivated.` : `${u.name} was deactivated.`);
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ create */

function CreateUserDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (user: ManagedUser) => void;
}) {
  const create = useCreateUser();
  const { reset } = create;
  // A refusal from one attempt must not greet the next.
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);
  return (
    <Dialog open={open} title="Add user" onClose={onClose} dismissible={!create.isPending}>
      <CreateUserForm create={create} onClose={onClose} onDone={onDone} />
    </Dialog>
  );
}

function CreateUserForm({
  create,
  onClose,
  onDone,
}: {
  create: ReturnType<typeof useCreateUser>;
  onClose: () => void;
  onDone: (user: ManagedUser) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [globalRole, setGlobalRole] = useState<GlobalRole>('user');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (create.isPending) return;
    create.mutate(
      { email: email.trim(), name: name.trim(), password, globalRole },
      { onSuccess: onDone },
    );
  };

  const problems = detailList(create.error, 'problems');

  return (
    <form className="bp-dialog-form" onSubmit={submit} noValidate>
      <label className="field">
        <span className="field-label">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
      </label>
      <label className="field">
        <span className="field-label">Email address</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="off"
        />
      </label>
      <label className="field">
        <span className="field-label">Temporary password</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
        />
        <span className="adm-help">
          At least 12 characters and not a common password. They will be made to replace it when
          they first sign in.
        </span>
      </label>
      <label className="field">
        <span className="field-label">Role</span>
        <select value={globalRole} onChange={(e) => setGlobalRole(e.target.value as GlobalRole)}>
          <option value="user">{ROLE_LABEL.user}</option>
          <option value="admin">{ROLE_LABEL.admin}</option>
        </select>
        <span className="adm-help">
          {globalRole === 'admin'
            ? 'An administrator can open every item and manage users, clients and access.'
            : 'A user sees nothing until they are given access on the Access screen.'}
        </span>
      </label>
      {problems.length > 0 ? (
        <ProblemList problems={problems} />
      ) : create.isError ? (
        <FormError>{errorText(create.error)}</FormError>
      ) : null}
      <div className="bp-dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={create.isPending}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn primary"
          disabled={create.isPending || !email.trim() || !name.trim() || !password}
        >
          {create.isPending ? 'Adding…' : 'Add user'}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------- edit */

function EditUserDialog({
  user,
  isMe,
  onClose,
  onDone,
}: {
  user: ManagedUser | null;
  isMe: boolean;
  onClose: () => void;
  onDone: (user: ManagedUser) => void;
}) {
  const update = useUpdateUser();
  const { reset } = update;
  useEffect(() => {
    if (user === null) reset();
  }, [user, reset]);
  return (
    <Dialog
      open={user !== null}
      title={user ? `Edit ${user.name}` : 'Edit user'}
      onClose={onClose}
      dismissible={!update.isPending}
    >
      {user ? <EditUserForm user={user} isMe={isMe} update={update} onClose={onClose} onDone={onDone} /> : null}
    </Dialog>
  );
}

function EditUserForm({
  user,
  isMe,
  update,
  onClose,
  onDone,
}: {
  user: ManagedUser;
  isMe: boolean;
  update: ReturnType<typeof useUpdateUser>;
  onClose: () => void;
  onDone: (user: ManagedUser) => void;
}) {
  const [name, setName] = useState(user.name);
  const [globalRole, setGlobalRole] = useState<GlobalRole>(user.globalRole);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (update.isPending) return;
    const patch: { id: string; name?: string; globalRole?: GlobalRole } = { id: user.id };
    if (name.trim() !== user.name) patch.name = name.trim();
    if (globalRole !== user.globalRole) patch.globalRole = globalRole;
    if (!patch.name && !patch.globalRole) {
      onClose();
      return;
    }
    update.mutate(patch, { onSuccess: onDone });
  };

  return (
    <form className="bp-dialog-form" onSubmit={submit} noValidate>
      <label className="field">
        <span className="field-label">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
      </label>
      <div className="field">
        <span className="field-label">Email address</span>
        <span className="adm-static">{user.email}</span>
        <span className="adm-help">The email address cannot be changed here.</span>
      </div>
      {isMe ? (
        <div className="field">
          <span className="field-label">Role</span>
          <span className="adm-static">{ROLE_LABEL[user.globalRole]}</span>
          <span className="adm-help">
            You cannot change your own role. Another administrator can do it for you.
          </span>
        </div>
      ) : (
        <label className="field">
          <span className="field-label">Role</span>
          <select value={globalRole} onChange={(e) => setGlobalRole(e.target.value as GlobalRole)}>
            <option value="user">{ROLE_LABEL.user}</option>
            <option value="admin">{ROLE_LABEL.admin}</option>
          </select>
          {globalRole === 'admin' && user.globalRole !== 'admin' ? (
            <span className="adm-help">
              An administrator can open every item. Any access grants this person holds stay
              in place but have no effect while they are an administrator.
            </span>
          ) : null}
        </label>
      )}
      {update.isError ? <FormError>{errorText(update.error)}</FormError> : null}
      <div className="bp-dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={update.isPending}>
          Cancel
        </button>
        <button type="submit" className="btn primary" disabled={update.isPending || !name.trim()}>
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/* ---------------------------------------------------------- reset password */

function ResetPasswordDialog({
  user,
  onClose,
  onDone,
}: {
  user: ManagedUser | null;
  onClose: () => void;
  onDone: (user: ManagedUser) => void;
}) {
  const reset = useResetPassword();
  const { reset: clear } = reset;
  useEffect(() => {
    if (user === null) clear();
  }, [user, clear]);
  return (
    <Dialog
      open={user !== null}
      title={user ? `Reset password for ${user.name}` : 'Reset password'}
      onClose={onClose}
      dismissible={!reset.isPending}
    >
      {user ? <ResetForm user={user} reset={reset} onClose={onClose} onDone={onDone} /> : null}
    </Dialog>
  );
}

function ResetForm({
  user,
  reset,
  onClose,
  onDone,
}: {
  user: ManagedUser;
  reset: ReturnType<typeof useResetPassword>;
  onClose: () => void;
  onDone: (user: ManagedUser) => void;
}) {
  const [password, setPassword] = useState('');
  const problems = detailList(reset.error, 'problems');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (reset.isPending) return;
    reset.mutate({ id: user.id, newPassword: password }, { onSuccess: () => onDone(user) });
  };

  return (
    <form className="bp-dialog-form" onSubmit={submit} noValidate>
      <p>
        This signs {user.name} ({user.email}) out everywhere and makes them choose a new password
        when they next sign in.
      </p>
      <label className="field">
        <span className="field-label">Temporary password</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
        />
        <span className="adm-help">At least 12 characters and not a common password.</span>
      </label>
      {problems.length > 0 ? (
        <ProblemList problems={problems} />
      ) : reset.isError ? (
        <FormError>{errorText(reset.error)}</FormError>
      ) : null}
      <div className="bp-dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={reset.isPending}>
          Cancel
        </button>
        <button type="submit" className="btn primary" disabled={reset.isPending || !password}>
          {reset.isPending ? 'Resetting…' : `Reset password for ${user.name}`}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------- deactivate / reactivate */

function ActiveDialog({
  user,
  activate,
  onClose,
  onDone,
}: {
  user: ManagedUser | null;
  activate: boolean;
  onClose: () => void;
  onDone: (user: ManagedUser, activated: boolean) => void;
}) {
  const terms = useTerms();
  const update = useUpdateUser();

  // The mutation lives here so a failure from one attempt does not leak into the next.
  const closeAndReset = () => {
    update.reset();
    onClose();
  };

  return (
    <ConfirmDialog
      open={user !== null}
      title={user ? `${activate ? 'Reactivate' : 'Deactivate'} ${user.name}?` : ''}
      confirmLabel={user ? `${activate ? 'Reactivate' : 'Deactivate'} ${user.name}` : ''}
      danger={!activate}
      busy={update.isPending}
      error={update.isError ? errorText(update.error) : null}
      onClose={closeAndReset}
      onConfirm={() => {
        if (!user) return;
        update.mutate(
          { id: user.id, isActive: activate },
          {
            onSuccess: (u) => {
              update.reset();
              onDone(u, activate);
            },
          },
        );
      }}
      body={
        user ? (
          activate ? (
            <p>
              {user.name} ({user.email}) will be able to sign in again, and any access they still
              hold on {terms.plural('blueprint')} will work again.
            </p>
          ) : (
            <p>
              {user.name} ({user.email}) will be signed out at once and cannot sign in. Their access
              to {terms.plural('blueprint')} is kept but does nothing until they are reactivated.
            </p>
          )
        ) : null
      }
    />
  );
}
